import React, { useEffect, useMemo, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiFileOutline } from '@mdi/js';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { IAppReduxStore } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { headingFontBold, vars } from '../../../Theme';
import { useTranslation } from 'react-i18next';
import { useMousePosition } from '../../../hooks/useMousePosition';

const boxSx: SxProps = {
    zIndex: 1201,
    position: 'absolute',
    px: '12px',
    py: '8px',
    borderRadius: '4px',
    background: vars.brandGreen,
};

const labelSx: SxProps = {
    ...headingFontBold,
    color: vars.white,
};

interface IDragPreviewProps {
    label?: string;
    dragCount?: number;
}

export default ({ label, dragCount }: IDragPreviewProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'dragPreview' });
    const dndActive = useSelector((store: IAppReduxStore) => store.dragDrop.dndActive);
    const mouseCoords = useMousePosition();
    const preview = useRef<HTMLDivElement>(null);

    const offset = useMemo(() => {
        return preview.current
            ? {
                  x: preview.current.clientWidth / 2,
                  y: preview.current.clientHeight + 6,
              }
            : null;
    }, [preview.current]);

    const maxBounds = useMemo(() => {
        return offset
            ? {
                  left: offset.x,
                  right: window.innerWidth - 12 - offset.x,
                  top: offset.y,
                  bottom: window.innerHeight - 12,
              }
            : null;
    }, [offset]);

    useEffect(() => {
        if (!offset || !maxBounds) return;

        const boundCoords = {
            x:
                mouseCoords.x < maxBounds.left
                    ? maxBounds.left
                    : mouseCoords.x > maxBounds.right
                    ? maxBounds.right
                    : mouseCoords.x,
            y:
                mouseCoords.y < maxBounds.top
                    ? maxBounds.top
                    : mouseCoords.y > maxBounds.bottom
                    ? maxBounds.bottom
                    : mouseCoords.y,
        };

        preview.current.style.left = `${boundCoords.x - offset.x}px`;
        preview.current.style.top = `${boundCoords.y - offset.y}px`;
    }, [mouseCoords, offset, maxBounds]);

    if (!dndActive || !mouseCoords) {
        return <></>;
    }

    return (
        <Box
            ref={preview}
            sx={{ ...boxSx, visibility: !offset || !maxBounds ? 'hidden' : undefined }}
        >
            <Stack direction='row'>
                <Icon path={mdiFileOutline} size={1.0} color={vars.white} />
                <Typography sx={labelSx}>
                    {label ??
                        (dragCount > 1
                            ? t('defaultLabelMultiple', { count: dragCount })
                            : t('defaultLabelSingle'))}
                </Typography>
            </Stack>
        </Box>
    );
};
